import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useFetch from '../../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram, faLinkedin, faTwitter, faYoutube, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import logo from '../../../assets/logo.png';
import { faLink, faVolumeHigh, faDownload, faStop } from '@fortawesome/free-solid-svg-icons';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';

// Public, unauthenticated counterpart to PDTalkingCard.
// Reached via /pub/talking/card/:code — anyone scanning the QR lands here, no login.

const PDTalkingCardPublic = () => {
  const { code } = useParams();
  const [fetchData] = useFetch();

  const [profile, setProfile] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [notPublished, setNotPublished] = useState(false);

  const [ttsLang, setTtsLang] = useState("en-IN");
  const [isSpeaking, setIsSpeaking] = useState(false);

  const cardRef = useRef(null);

  const fetchPublicProfile = useCallback(() => {
    const config = {
      url: `/myprofile/pub/talking/card/${code}`,
      method: 'get',
      showErrorToast: false,
    };

    fetchData(config)
      .then(response => {     
        if (response && response.status) {
          setProfile(response.myProfile);
          setBusiness(response.myProfile.business || null);
          document.title = `Think5Whys Digital Card - ${response.myProfile.userName}`;
        } else if (response?.notPublished) {
          setNotPublished(true);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [code, fetchData]);

  useEffect(() => {
    fetchPublicProfile();
  }, [fetchPublicProfile]);

  const speakCard = () => {
    if (!profile) return;
    const msg = new SpeechSynthesisUtterance();
    msg.lang = ttsLang;

    // Reads out everything meaningful the founder has filled in — skips blanks.
    const parts = [
      `Introducing the digital identity card of ${profile.userName}!`,
      profile.purpose && `Purpose: ${profile.purpose}.`,
      profile.journeySoFar && `Journey so far: ${profile.journeySoFar}.`,
      profile.dreams && `Dreams: ${profile.dreams}.`,
      profile.challenges && `Challenges: ${profile.challenges}.`,
      profile.innovations && `Innovations: ${profile.innovations}.`,
      profile.spotlights && `Spotlights: ${profile.spotlights}.`,
      profile.brands && `Brands: ${profile.brands}.`,
      profile.supportRequests && `Support requested: ${profile.supportRequests}.`,
      business?.businessName && `This profile is linked to ${business.businessName}.`,
      `This profile belongs to ${profile.userName}, part of the Think 5 Whys ecosystem.`,
    ].filter(Boolean);

    msg.text = parts.join(' ');
    msg.onend = () => setIsSpeaking(false);
    msg.onerror = () => setIsSpeaking(false);

    speechSynthesis.cancel();
    speechSynthesis.speak(msg);
    setIsSpeaking(true);
  };

  const stopSpeaking = () => {
    speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const downloadCard = () => {
    if (!cardRef.current) return;
    const buttons = cardRef.current.querySelectorAll('.hide-for-download');
    buttons.forEach(btn => btn.style.display = 'none');

    html2canvas(cardRef.current, { scale: 2 }).then(canvas => {
      const link = document.createElement('a');
      link.download = `${profile.userName}_DigitalCard.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      buttons.forEach(btn => btn.style.display = '');
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh] bg-gray-900 text-white">
        <p className="text-sm text-gray-300">Loading card...</p>
      </div>
    );
  }

  if (notPublished) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[50vh] bg-gray-900 text-white p-4 text-center">
        <img src={logo} alt="Think5Whys" className="w-10 h-10 mb-3 opacity-70" />
        <p className="text-sm text-gray-300">This card is being set up and isn't ready to view yet.</p>
        <p className="text-xs text-gray-500 mt-1">Please check back shortly.</p>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[50vh] bg-gray-900 text-white p-4 text-center">
        <img src={logo} alt="Think5Whys" className="w-10 h-10 mb-3 opacity-70" />
        <p className="text-sm text-gray-300">We couldn't find this card.</p>
        <p className="text-xs text-gray-500 mt-1">Double-check the code and try again.</p>
      </div>
    );
  }

  const social = profile.socialProfiles || {};
  const qrValue = `${window.location.origin}/pub/talking/card/${profile.userCode}`;

  return (
    <div className="flex flex-col justify-center items-center space-y-6 bg-gray-900 p-4 rounded-xl text-white border-t-2 border-cyan-200 mt-2">
      <div
        ref={cardRef}
        className="bg-gradient-to-r from-gray-200 to-gray-400 border border-gray-600 rounded-lg shadow-lg w-full max-w-sm p-4 flex flex-col items-center"
      >
        <img
          src={`/api/downloads/users/profilepic/${profile.userCode}.webp`}
          alt="Profile"
          className="w-24 h-24 rounded-full border-2 border-gray-300 p-1 mb-4 object-cover"
          onError={(e) => {
            e.target.onerror = null;          // prevent infinite loop
            e.target.src = logo;
          }}
        />

        <h2 className="text-gray-800 font-bold text-2xl mb-2 text-center">{profile.userName}</h2>
        <p className="text-sm text-gray-600 text-center">ID#: {profile.userCode}</p>
        {profile.address && <p className="text-sm text-gray-600 text-center">{profile.address}</p>}

        {business?.businessName && (
          <p className="text-sm text-gray-700 font-semibold text-center mt-2">{business.businessName}</p>
        )}
        {business?.email && <p className="text-sm text-gray-600 text-center">Email: {business.email}</p>}
        {business?.phone && <p className="text-sm text-gray-600 text-center">Mobile: {business.phone}</p>}
        {business?.city && <p className="text-sm text-gray-600 text-center mb-2">Place: {business.city}</p>}

        {/* Full narrative fields — all exposed, each skipped if blank */}
        <div className="w-full text-left text-xs text-gray-700 space-y-1 my-3 border-t border-gray-400 pt-2">
          {profile.purpose && <p><span className="font-semibold">Purpose:</span> {profile.purpose}</p>}
          {profile.journeySoFar && <p><span className="font-semibold">Journey So Far:</span> {profile.journeySoFar}</p>}
          {profile.dreams && <p><span className="font-semibold">Dreams:</span> {profile.dreams}</p>}
          {profile.challenges && <p><span className="font-semibold">Challenges:</span> {profile.challenges}</p>}
          {profile.innovations && <p><span className="font-semibold">Innovations:</span> {profile.innovations}</p>}
          {profile.spotlights && <p><span className="font-semibold">Spotlights:</span> {profile.spotlights}</p>}
          {profile.brands && <p><span className="font-semibold">Brands:</span> {profile.brands}</p>}
          {profile.supportRequests && <p><span className="font-semibold">Support Requested:</span> {profile.supportRequests}</p>}
        </div>

        <div className="flex justify-around mb-4 flex-wrap gap-2">
          {social.facebook && (
            <a href={social.facebook} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon className="text-blue-600" icon={faFacebook} size="lg" />
            </a>
          )}
          {social.instagram && (
            <a href={social.instagram} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon className="text-pink-600" icon={faInstagram} size="lg" />
            </a>
          )}
          {social.x && (
            <a href={social.x} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon className="text-blue-400" icon={faTwitter} size="lg" />
            </a>
          )}
          {social.linkedin && (
            <a href={social.linkedin} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon className="text-blue-700" icon={faLinkedin} size="lg" />
            </a>
          )}
          {social.youtube && (
            <a href={social.youtube} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon className="text-red-600" icon={faYoutube} size="lg" />
            </a>
          )}
          {(social.whatsapp || business?.whatsapp) && (
            <a href={`https://wa.me/${(social.whatsapp || business.whatsapp).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon className="text-green-600" icon={faWhatsapp} size="lg" />
            </a>
          )}
          {(social.website || business?.website) && (
            <a href={social.website || business.website} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon className="text-gray-600" icon={faLink} size="lg" />
            </a>
          )}
        </div>

        <div className="bg-white p-2 rounded-md shadow-md mb-4">
          <QRCode value={qrValue} size={96} />
        </div>

        <p className="bg-gray-100 text-gray-700 text-center text-xs p-1 rounded-md mb-1 flex items-center justify-center">
          Powered by PurpleDoor@
          <img src={logo} alt="Think5Whys" className="inline-block w-4 h-4 mx-2" />
          think5whys.com
        </p>
      </div>

      <div className="flex flex-row justify-center w-full">
        <button
          onClick={downloadCard}
          className="w-40 text-xs lg:text-sm bg-green-600 text-white px-2 py-1 rounded shadow flex items-center gap-2 justify-center"
        >
          <FontAwesomeIcon icon={faDownload} />
          Download
        </button>
      </div>

      <div className="p-1 border-t border-blue-400">
        <p className="text-yellow-200 text-[10px]">The Voice Is As Per Your Browser. This can be treated as fun!</p>
      </div>

      <div className="flex flex-row items-center hide-for-download">
        <select
          className="bg-white text-gray-700 px-2 py-1 rounded border w-40 mt-1 text-xs"
          value={ttsLang}
          onChange={(e) => setTtsLang(e.target.value)}
        >
          <option value="en-IN">English</option>
        </select>
        <button
          onClick={isSpeaking ? stopSpeaking : speakCard}
          className={`text-xs lg:text-sm w-40 px-2 py-1 rounded shadow flex items-center gap-2 justify-center ${
            isSpeaking ? "bg-red-600" : "bg-blue-600"
          } text-white`}
        >
          <FontAwesomeIcon icon={isSpeaking ? faStop : faVolumeHigh} />
          {isSpeaking ? "Stop" : "Listen"}
        </button>
      </div>
    </div>
  );
};

export default PDTalkingCardPublic;